import escapeHtml from 'escape-html';
import Container from 'pisces/components/Container';
import Heading from 'pisces/components/Heading';
import Text from 'pisces/components/Text';
import { Link as LinkHelper } from 'prismic-helpers';
import PrismicRichText, { Elements } from 'prismic-richtext';
import { compose, concat, equals, prop, toLower } from 'ramda';
import React, { Fragment } from 'react';
import { filterFalsy } from 'yafrpl/arrays';

import Link from '../../components/library/Link';
import { toDocType } from '../../schema/prismic/mapping';
import { LINK_TYPE_DOC, LINK_TYPE_MEDIA } from '../../types/link';
import { getUrlFromDocument } from '../routes/mapping';

const CLASS_NAME = 'generated';

const serializeStandardTag = (tag, props, children) => (
  <Container as={tag} {...props}>
    {children}
  </Container>
);

const serializeText = (props, children) => <Text {...props} body={children} />;

const serializeHeading = (tag, props, children) => (
  <Heading as={tag} {...props}>
    {children}
  </Heading>
);

const serializeHyperlink = (props, linkResolver, { data } = {}, children) => {
  const type = compose(concat('link-'), toLower, prop('link_type'))(data);
  let link = LinkHelper.url(data, linkResolver);

  if (equals(type, LINK_TYPE_MEDIA)) {
    props.download = true;
  }

  if (equals(type, LINK_TYPE_DOC)) {
    link = getUrlFromDocument({
      ...data,
      linkType: type,
      type: toDocType(data.type),
    });
  }

  return (
    <Link {...props} href={link}>
      {children}
    </Link>
  );
};

const normalizeChildren = (children) => {
  return children.map((child) => {
    if (typeof child === 'string') {
      const jsxChild = child
        .replace(/&#39;/g, "'")
        .replace(/&quot;/g, '"')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .split('<br />')
        .reduce((acc, val) => {
          acc.push(val);
          acc.push(<br />);
          return acc;
        }, []);
      jsxChild.pop();
      return jsxChild;
    }
    return child;
  });
};

const serializeSpan = (content) =>
  content ? escapeHtml(content).replace(/\n/g, '<br />') : '';

const serialize = (linkResolver, type, element, content, children, index) => {
  const props = {
    className: filterFalsy([CLASS_NAME, element.label]).join(' '),
    key: index,
  };
  const normalizedChildren = normalizeChildren(children);

  switch (type) {
    case Elements.heading1:
      return serializeHeading('h1', props, normalizedChildren);
    case Elements.heading2:
      return serializeHeading('h2', props, normalizedChildren);
    case Elements.heading3:
      return serializeHeading('h3', props, normalizedChildren);
    case Elements.heading4:
      return serializeHeading('h4', props, normalizedChildren);
    case Elements.heading5:
      return serializeHeading('h5', props, normalizedChildren);
    case Elements.heading6:
      return serializeHeading('h6', props, normalizedChildren);
    case Elements.paragraph:
      return serializeText(props, normalizedChildren);
    case Elements.preformatted:
      return serializeStandardTag('pre', props, escapeHtml(element.text));
    case Elements.strong:
      return serializeStandardTag('strong', props, normalizedChildren);
    case Elements.em:
      return serializeStandardTag('em', props, normalizedChildren);
    case Elements.listItem:
      return serializeStandardTag(
        'li',
        { ...props, className: `${props.className} list-element-item` },
        normalizedChildren
      );
    case Elements.oListItem:
      return serializeStandardTag('li', props, normalizedChildren);
    case Elements.list:
      return serializeStandardTag(
        'ul',
        { ...props, className: `${props.className} list-element` },
        normalizedChildren
      );
    case Elements.oList:
      return serializeStandardTag('ol', props, normalizedChildren);
    case Elements.hyperlink:
      return serializeHyperlink(
        props,
        linkResolver,
        element,
        normalizedChildren
      );
    case Elements.label:
      return serializeStandardTag('span', props, normalizedChildren);
    case Elements.span:
      return serializeSpan(content);
    case Elements.image:
    case Elements.embed:
    default:
      return null;
  }
};

export const asHtml = (richText, linkResolver, htmlSerializer) => {
  const serializedChildren = PrismicRichText.serialize(
    richText,
    serialize.bind(null, linkResolver),
    htmlSerializer
  );
  return <Fragment>{serializedChildren}</Fragment>;
};
